import { DashboardHeader } from '@/components/dashboard/dashboard-header'
import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useTRPC } from '@/trpc/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { InfoIcon } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/dashboard/admin/users_old/')({
  component: RouteComponent,
})

function RouteComponent() {
  const api = useTRPC()
  const queryClient = useQueryClient()

  const { data: usersInBusiness, isPending } = useQuery({
    ...api.user.getUsers.queryOptions(),
  })
  const userQueryKey = api.user.getUsers.queryKey()

  // TODO: we won't be changing roles, instead we'll be adding/deleting

  // @ts-expect-error - idk wtf, but it's complaining about undefined shit or smth
  const { mutate: changeUserRole } = useMutation({
    ...api.user.changeUserRole.mutationOptions(),
    onSuccess: async () => {
      toast.success('User role updated successfully')
      await queryClient.invalidateQueries({ queryKey: userQueryKey })
    },
    onError: (error) => {
      console.error(error)
    },
  })

  const [selectedRoles, setSelectedRoles] = useState<{ [key: string]: string | null }>({})

  const handleRoleChange = (userId: string, newRole: string) => {
    setSelectedRoles((prev) => ({
      ...prev,
      [userId]: newRole,
    }))
  }

  const handleSaveRole = (userId: string) => {
    console.log('... inside handleSaveRole')
    console.log({ userId })
    const newRole = selectedRoles[userId]
    if (!newRole) return

    changeUserRole({
      userId,
      newRole: newRole as
        | 'employee'
        | 'client'
        | 'admin'
        | 'hr_admin'
        | 'hr_recruitment'
        | 'team_leader',
    })
  }

  return (
    <DashboardShell>
      <DashboardHeader heading="Users" text="Manage your organization's users." />
      <div className="space-y-4">
        <Alert>
          <InfoIcon className="h-4 w-4" />
          <AlertDescription>
            Users will need to log out and log back in for role changes to take effect.
          </AlertDescription>
        </Alert>
        <Table>
          <TableCaption>List of Users</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>Name from Google Profile</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Onboarded</TableHead>
              <TableHead>
                <div className="flex items-center gap-2">
                  Default Role
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <InfoIcon className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          <Link to="/dashboard/admin/info" className="underline text-blue-500">
                            Click here
                          </Link>{' '}
                          to learn more about roles
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isPending ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center">
                  <Skeleton className="h-40 w-full" />
                </TableCell>
              </TableRow>
            ) : (
              usersInBusiness?.map((user) => (
                <TableRow key={user.userId}>
                  <TableCell className="font-medium">{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {user.isUserOnboarded ? (
                      <span className="text-green-600 dark:text-green-500 font-medium">Yes</span>
                    ) : (
                      <span className="text-yellow-600 dark:text-yellow-500 font-medium">No</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Select
                      value={selectedRoles[user.userId] ?? user.role ?? ''}
                      onValueChange={(value) => handleRoleChange(user.userId, value)}
                      disabled={user.role === 'admin'}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="hr_admin">HR Admin</SelectItem>
                        <SelectItem value="hr_recruitment">HR Recruitment</SelectItem>
                        {/* <SelectItem value="team_leader">Team Leader</SelectItem> */}
                        <SelectItem value="employee">Employee</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSaveRole(user.userId)}
                      disabled={
                        !selectedRoles[user.userId] || selectedRoles[user.userId] === user.role
                      }
                    >
                      Save
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </DashboardShell>
  )
}
